"use client";

import * as React from "react";
import {memo, useEffect, useMemo, useState} from "react";
import {useRouter} from "next/navigation";
import {PriceRialProps, Product} from "@/types/product";
import {useAppSelector} from "@/redux/hooks";
import {numberWithCommas} from "@/lib/helper-functions";
import {toSlug} from "@/utils/string-formatter";
import {separateCompany} from "@/utils/separate-company";
import {PATHS} from "@/utils/paths";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {Tooltip, useMediaQuery} from "@mui/material";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import {useTheme} from "@mui/material/styles";
import FaIcon from "@/components/utilities/fa-icon";
import PurchasingControl from "./product-card-purchasing-control";
import PictureWithBlur from "../utilities/picture-with-blur";
import ProductCardTitle from "./product-card-title";
import ProductCardSubtitle from "./product-card-subtitle";
import Link from "next/link";
import ProductLoginButton from "@/components/product-card/product-button-login";


type ProductCardVariant = "grid" | "list";

function calcDiscountedPrice(price: number, discount: number): number {
    const p = Number(price) || 0;
    const d = Number(discount) || 0;

    if (p <= 0) return 0;
    if (d <= 0) return p;

    const dd = Math.min(100, Math.max(0, d));
    return Math.round((p * (100 - dd)) / 100);
}


function PriceRial({
                       price,
                       discount = 0,
                       discounted = true,
                       showUnit = true,
                       showIfZero = "بدون قیمت",
                   }: PriceRialProps) {


    const value = useMemo(() => {
        if (!discounted) return Math.round(Number(price) || 0);
        return calcDiscountedPrice(price, discount);
    }, [price, discount, discounted]);

    if (value <= 0) return <>{showIfZero}</>;

    return (
        <Box
            component = "span"
            sx = {{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5
        }}
        >
            {numberWithCommas(value)}
            {showUnit && (
                <Box component = "span" sx = {{fontSize: 14, pt: .2}} >
                    ریال
                </Box >
            )}
        </Box >

    );
}

function ProductCardUnified({
                                productData,
                                offer,
                                variant = "grid",
                                slider = false,
                                priorityImage = false,
                            }: {
    productData: Product;
    offer?: boolean;
    slider?: boolean;
    variant?: ProductCardVariant;
    priorityImage?: boolean;
}) {
    const router = useRouter();
    const theme = useTheme();
    const [isClient, setIsClient] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const user = useAppSelector((state) => state.user.currentUser);


    useEffect(() => {
        setIsClient(true);
    }, []);

    const {
        id,
        namePersian,
        techNumber,
        price,
        isExists,
        company,
        car,
        isNewest,
        discount,
        picture,
        isOffer,
        count,
    } = productData;

    const separated = useMemo(() => separateCompany(company), [company]);

    const productUrl = useMemo(
        () => PATHS.GENERAL.PRODUCT(id, toSlug(namePersian)),
        [id, namePersian]
    );

    // ----------------------------
    // 1) GRID (your ProductCard)
    // ----------------------------
    const renderGrid = () => {

        return (
            <Card
                className = {!isClient && !priorityImage ? "fade-slide-in" : ""}
                elevation = {0}
                sx = {{
                    transform: "scale(1)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                        transform: "scale(1.025)",
                    },
                }}
            >
                <Box
                    display = "flex"
                    flexDirection = "column"
                    height = "100%"
                >
                    <Box
                        display = "flex"
                        flexDirection = "column"
                        alignItems = "center"
                        height = "100%"
                        p = {2}
                    >
                        <Box
                            display = "flex"
                            flexDirection = "column"
                            alignItems = "center"
                            width = "100%"
                            component = {Link}
                            href = {productUrl}
                            sx = {{
                                cursor: "pointer",
                            }}
                        >
                            <Box
                                display = "flex"
                                justifyContent = "space-between"
                                alignItems = "center"
                                width = "100%"
                            >
                                <Box
                                    display = "flex"
                                    alignItems = "center"
                                    width = "100%"
                                    pr = {1}
                                    gap = {1}
                                    height = {24}
                                >
                                    {discount > 0 && (
                                        <Box
                                            display = "flex"
                                            alignItems = "center"
                                            justifyContent = "center"
                                            fontWeight = {500}
                                            px = {0.2}
                                            sx = {{
                                                lineHeight: 1,
                                                border: `1.6px solid ${theme.palette.primary.light}`,
                                                borderRadius: 0,
                                                color: "primary.light",
                                            }}
                                        >
                                            <Typography fontSize = {11} sx = {{lineHeight: 1.3}} >
                        <span
                            style = {{
                                fontWeight: 500,
                                fontSize: 13,
                                fontFamily: "sans-serif",
                            }}
                        >
                          {"%" + discount + " "}
                        </span >
                                            </Typography >
                                        </Box >
                                    )}

                                    <Typography
                                        fontWeight = {400}
                                        fontSize = {12}
                                        pt = {0.5}
                                        color = {theme.palette.primary.light}
                                    >
                                        {separated.english}
                                    </Typography >
                                </Box >

                                <Stack
                                    direction = "row"
                                    gap = {1}
                                    divider = {<Divider orientation = "vertical" flexItem />}
                                >
                                    {isNewest && (
                                        <Tooltip title = "کالای تازه رسیده" >
                                            <IconButton
                                                disableRipple
                                                sx = {{
                                                    p: 0,
                                                    color: "primary.light",
                                                    transition: "transform 0.3s ease, color 0.3s ease",
                                                    "&:hover": {
                                                        transform: "scale(1.15) rotate(-5deg)",
                                                        color: theme.palette.primary.dark,
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                            >
                                                <FaIcon
                                                    faIcon = "fa-dolly"
                                                    variant = "light"
                                                    fontSize = {20}
                                                />
                                            </IconButton >
                                        </Tooltip >
                                    )}

                                    {isOffer && (
                                        <Tooltip title = "پیشنهاد ویژه" >
                                            <IconButton
                                                disableRipple
                                                sx = {{
                                                    p: 0,
                                                    color: "primary.light",
                                                    transition: "transform 0.3s ease, color 0.3s ease",
                                                    "&:hover": {
                                                        transform: "scale(1.15) rotate(5deg)",
                                                        color: theme.palette.primary.dark,
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                            >
                                                <FaIcon
                                                    faIcon = "fa-trophy-alt"
                                                    fontSize = {20}
                                                    variant = "light"
                                                />
                                            </IconButton >
                                        </Tooltip >
                                    )}
                                </Stack >
                            </Box >

                        </Box >

                        <Box
                            onClick = {() => {
                                router.push(`${productUrl}`);
                            }}
                            sx = {{cursor: "pointer"}}
                            display = "flex"
                            flexDirection = "column"
                            alignItems = "center"
                            height = {{sm: 260, xs: 220}}
                        >
                            <Box mt = {1} >
                                <PictureWithBlur
                                    src = {picture}
                                    width = {170}
                                    height = {170}
                                    wrapperSx = {{
                                        width: {xs: 130, sm: 170},
                                        height: {xs: 130, sm: 170},
                                    }}
                                    alt = {namePersian}
                                    priority = {priorityImage}
                                />
                            </Box >

                            <ProductCardTitle name = {namePersian} car = {car} centerText />
                            <ProductCardSubtitle company = {separated.persian} techNumber = {techNumber} />

                        </Box >

                        <Box flexGrow = {1} />

                        <Box
                            position = "relative"
                            alignItems = "end"
                            alignContent = "end"
                            justifyContent = "space-between"
                            display = "flex"
                            width = "100%"
                            height = {68}
                            gap = {isOffer ? 0 : isExists && user ? 2 : 0}
                        >
                            {isExists && user && (
                                <Grid item sx = {{
                                    width: "100%",
                                    color: theme.palette.primary.main,

                                }} >
                                    <Box
                                        alignItems = "start"
                                        alignContent = "center"
                                        display = "flex"
                                        flexDirection = "column"
                                    >
                                        {discount > 0 && (
                                            <Typography
                                                fontSize = {{md: 16, xs: 16}}
                                                textAlign = "initial"
                                                color = {theme.palette.grey[600]}
                                                position = "relative"
                                                sx = {{
                                                    opacity: 0.6,
                                                    transition: "all 0.3s ease",
                                                    "&::before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: 0,
                                                        width: "100%",
                                                        borderTop: "1px solid",
                                                        borderColor: theme.palette.grey[600],
                                                        transform: "translateY(-50%)",
                                                    },
                                                    ".MuiCard-root:hover &": {
                                                        color: theme.palette.primary.dark,
                                                        opacity: 1,
                                                    },
                                                }}
                                            >
                                                {numberWithCommas(price)}

                                            </Typography >
                                        )}

                                        <Typography
                                            height = "100%"
                                            alignItems = "center"
                                            justifyContent = "center"
                                            textAlign = "center"
                                            display = "flex"
                                            fontWeight = {500}
                                            fontSize = {{md: 20, xs: 16}}
                                            color = {theme.palette.primary.main}
                                            gap = {.2}
                                            noWrap
                                        >
                                            <PriceRial price = {price} discount = {discount} />
                                        </Typography >
                                    </Box >

                                </Grid >
                            )}

                            <Box
                                display = "flex"
                                width = "100%"
                                justifyContent = {user && isExists ? "end" : "center"}
                            >
                                {user ? (
                                    <>
                                        {!isExists && (
                                            <Box
                                                display = "flex"
                                                justifyContent = "space-between"
                                                alignItems = "center"
                                                width = "100%"
                                            >
                                                <Typography
                                                    fontSize = {14}
                                                    fontWeight = {400}
                                                    color = {theme.palette.primary.dark}
                                                    noWrap
                                                >
                                                    افزودن به پیش خرید
                                                </Typography >
                                            </Box >
                                        )}

                                        <PurchasingControl
                                            offer = {offer}
                                            productName = {namePersian + " " + car}
                                            id = {id.toString()}
                                            preOrder = {!isExists}
                                            initialCount = {count}
                                        />
                                    </>
                                ) : (
                                    <Box textAlign = "center" >
                                        <Typography
                                            fontSize = {14}
                                            fontWeight = {300}
                                            color = {theme.palette.primary.main}
                                            mb = {1.5}
                                        >
                                            برای مشاهده قیمت ابتدا وارد شوید
                                        </Typography >
                                        <ProductLoginButton />
                                    </Box >
                                )}
                            </Box >
                        </Box >
                    </Box >
                </Box >
            </Card >
        );
    };

    // --------------------------------
    // 2) LIST MOBILE (ProductCardMobile)
    // --------------------------------
    const renderListMobile = () => {
        return (
            <Card >
                <Box
                    width = "100%"
                    height = "100%"
                    p = {2}
                    pb = {0}
                    minHeight = {140}
                    sx = {{
                        cursor: "pointer",
                    }}
                    onClick = {() => router.push(productUrl)}
                >
                    <Box
                        display = "flex"
                        width = "100%"
                        pt = {0.5}
                        justifyContent = "space-between"
                        gap = {1}
                    >
                        <Box
                            display = "flex"
                            alignItems = "center"
                            justifyContent = "center"
                            color = "primary.light"
                            lineHeight = {1}
                            borderRadius = {0}
                            fontWeight = {500}
                        >
                            <Typography fontSize = {11} lineHeight = {1}
                                display = {discount > 0 ? "inline" : "none"} >
                  <span
                      style = {{
                          fontWeight: 500,
                          fontSize: 15,
                          fontFamily: "sans-serif",
                      }}
                  >
                    {"%" + discount + " "}
                  </span >
                            </Typography >
                        </Box >

                        {(isNewest || isOffer) && (
                            <Stack
                                direction = "row"
                                gap = {1}
                                divider = {<Divider orientation = "vertical" flexItem />}
                            >
                                {isNewest && (
                                    <Tooltip title = "کالای تازه رسیده" >
                                        <IconButton sx = {{color: theme.palette.primary.light, p: 0}} >
                                            <FaIcon
                                                faIcon = "fa-dolly"
                                                variant = "light"
                                                fontSize = {16}
                                            />
                                        </IconButton >
                                    </Tooltip >
                                )}

                                {isOffer && (
                                    <Tooltip title = "پیشنهاد ویژه" >
                                        <IconButton sx = {{color: theme.palette.primary.light, p: 0}} >
                                            <FaIcon
                                                justifyContent = "start"
                                                alignItems = "start"
                                                faIcon = "fa-trophy-alt"
                                                variant = "light"
                                                fontSize = {16}
                                            />
                                        </IconButton >
                                    </Tooltip >
                                )}
                            </Stack >
                        )}
                    </Box >

                    <Box display = "flex" textAlign = "center" my = {2} gap = {1} >

                        <Box mr = {1} >
                            <PictureWithBlur
                                src = {picture}
                                width = {86}
                                height = {86}
                                alt = {namePersian}
                                priority = {priorityImage}
                            />
                        </Box >

                        <Box
                            width = "100%"
                            mb = {2}
                            maxWidth = "unset"
                        >
                            <ProductCardTitle sx = {{mb: 1}} name = {namePersian} car = {car} />
                            <ProductCardSubtitle company = {company} techNumber = {techNumber} />

                        </Box >
                    </Box >
                </Box >

                <Box
                    display = "flex"
                    justifyContent = "space-between"
                    alignItems = "flex-end"
                    width = "100%"
                    px = {2}
                    pb = {2}
                >
                    {isExists && user && (
                        <Stack
                            direction = "row"
                            spacing = {1}
                            alignItems = "center"
                            width = "100%"
                        >
                            <Box >
                                {user && discount ? (
                                    <>
                                        <Typography
                                            fontSize = {16}
                                            color = {theme.palette.grey[600]}
                                            textAlign = "initial"
                                            position = "relative"
                                            sx = {{
                                                opacity: 0.6,
                                                transition: "all 0.3s ease",
                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: 0,
                                                    width: "60%",
                                                    borderTop: "1px solid",
                                                    borderColor: theme.palette.grey[600],
                                                    transform: "translateY(-50%)",
                                                },
                                                ".MuiCard-root:hover &": {
                                                    color: theme.palette.primary.dark,
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            {numberWithCommas(price)}
                                        </Typography >
                                    </>
                                ) : null}

                                <Typography
                                    fontWeight = {500}
                                    color = {theme.palette.primary.main}
                                    textAlign = "right"
                                    fontSize = {18}
                                    noWrap
                                >
                                    <PriceRial price = {price} discount = {discount} />
                                </Typography >

                            </Box >
                        </Stack >
                    )}

                    {user ? (
                        <Box display = "flex" height = "100%" width = "100%" >
                            {!isExists && (
                                <Box
                                    display = "flex"
                                    flexDirection = "column-reverse"
                                    width = "100%"
                                    gap = {0.5}
                                >
                                    <Typography
                                        fontSize = {14}
                                        fontWeight = {400}
                                        color = {theme.palette.primary.dark}
                                    >
                                        ثبت درخواست پیش‌خرید
                                    </Typography >
                                    <Typography
                                        fontSize = {12}
                                        fontWeight = {400}
                                        color = {theme.palette.primary.light}
                                    >
                                        موجود نیست
                                    </Typography >
                                </Box >
                            )}
                        </Box >
                    ) : null}

                    {user ? (
                        <Box display = "flex" height = "100%" >
                            <PurchasingControl
                                offer = {offer}
                                productName = {namePersian + " " + car}
                                id = {id.toString()}
                                preOrder = {!isExists}
                                initialCount = {count}
                            />
                        </Box >
                    ) : (
                        <Box
                            width = "100%"
                            display = "flex"
                            alignItems = "center"
                            justifyContent = "space-between"
                            gap = {1}
                        >
                            <Typography
                                fontSize = {14}
                                fontWeight = {300}
                                color = {theme.palette.primary.main}
                            >
                                برای مشاهده قیمت ابتدا وارد شوید
                            </Typography >
                            <ProductLoginButton />
                        </Box >
                    )}
                </Box >
            </Card >
        );
    };

    // ------------------------------------
    // 3) LIST DESKTOP (ProductCardListDesktop)
    // ------------------------------------
    const renderListDesktop = () => {
        const imageSize = 90;

        const handleClick = () => {
            if (typeof window !== "undefined") {
                router.push(PATHS.GENERAL.PRODUCT(id, toSlug(namePersian)));
            }
        };

        return (
            <Card >
                <Box
                    display = "flex"
                    gap = {2}
                    height = "100%"
                    width = "100%"
                    p = {2}
                >
                    <Box
                        onClick = {handleClick}
                        display = "flex"
                        flexDirection = "column"
                        justifyContent = "space-between"
                        width = "100%"
                        height = "100%"
                        sx = {{cursor: "pointer"}}
                    >
                        <Box display = "flex" justifyContent = "space-between" width = "100%" >
                            <Box gap = {1} display = "flex" alignItems = "center" width = "100%" >
                                {isExists && user && discount > 0 && (
                                    <Box gap = {1} display = "flex" alignItems = "center" height = {24} >
                                        <Box
                                            display = "flex"
                                            color = "primary.light"
                                            alignItems = "center"
                                            justifyContent = "center"
                                            fontWeight = {500}
                                            px = {0.2}
                                            lineHeight = {1}
                                            borderRadius = {0}
                                            border = {`1.6px solid ${theme.palette.primary.light}`}
                                        >
                                            <Typography fontSize = {11} lineHeight = {1.3} >
                        <span
                            style = {{
                                fontWeight: 500,
                                fontSize: 13,
                                fontFamily: "sans-serif",
                            }}
                        >{"%" + discount + " "}</span >
                                            </Typography >
                                        </Box >
                                    </Box >
                                )}

                                <Typography
                                    fontWeight = {400}
                                    fontSize = {12}
                                    pt = {0.5}
                                    color = {theme.palette.primary.light}
                                >
                                    {separated.english}
                                </Typography >
                            </Box >

                            <Stack
                                direction = "row"
                                divider = {<Divider orientation = "vertical" flexItem />}
                                right = {16}
                                gap = {1}
                                position = "absolute"
                            >
                                {isNewest && (
                                    <Tooltip title = "کالای تازه رسیده" >
                                        <IconButton
                                            sx = {{
                                                color: theme.palette.primary.light,
                                                p: 0,
                                                gap: 1,
                                                width: 24,
                                                pl: 0.5,
                                            }}
                                        >
                                            <FaIcon
                                                faIcon = "fa-dolly"
                                                variant = "light"
                                                fontSize = {20}
                                            />
                                        </IconButton >
                                    </Tooltip >
                                )}

                                {isOffer && (
                                    <Tooltip title = "پیشنهاد ویژه" >
                                        <IconButton
                                            sx = {{
                                                color: theme.palette.primary.light,
                                                p: 0,
                                                gap: 1,
                                                width: 24,
                                                pl: 0.5,
                                            }}
                                        >
                                            <FaIcon
                                                faIcon = "fa-trophy-alt"
                                                variant = "light"
                                                fontSize = {20}
                                            />
                                        </IconButton >
                                    </Tooltip >
                                )}
                            </Stack >
                        </Box >

                        <>
                            <Box display = "flex" position = "relative" width = "65%" >
                                <Box display = "flex" >
                                    <PictureWithBlur
                                        src = {picture}
                                        width = {imageSize}
                                        height = {imageSize}
                                        alt = {namePersian}
                                        priority = {priorityImage}
                                    />

                                    <Stack
                                        position = "relative"
                                        height = "100%"
                                        py = {1.5}
                                        ml = {1.2}
                                    >
                                        <ProductCardTitle
                                            sx = {{textAlign: "left"}}
                                            name = {namePersian}
                                            car = {car}
                                            centerText
                                        />

                                        <ProductCardSubtitle
                                            company = {separated.persian}
                                            techNumber = {techNumber}
                                        />
                                    </Stack >
                                </Box >
                            </Box >

                            {isExists && user ? (
                                <Stack
                                    display = "flex"
                                    alignItems = "end"
                                    justifyContent = "flex-end"
                                    position = "absolute"
                                    flexDirection = "row"
                                    gap = {2}
                                    right = {140}
                                    bottom = {16}
                                    pr = {variant === "list" ? 2 : 0}
                                >
                                    <Stack position = "relative" display = "flex" >
                                        {discount > 0 && price > 0 && (
                                            <Typography
                                                color = {theme.palette.primary.main}
                                                fontWeight = {300}
                                                fontSize = {16}
                                                sx = {{
                                                    mb: 0.3,
                                                    textAlign: "initial",
                                                    color: theme.palette.grey[600],
                                                    opacity: 0.6,
                                                    transition: "all 0.3s ease",
                                                    "&::before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: 0,
                                                        width: "100%",
                                                        borderTop: "1px solid",
                                                        borderColor: theme.palette.grey[600],
                                                        transform: "translateY(-50%)",
                                                    },
                                                    ".MuiCard-root:hover &": {
                                                        color: theme.palette.primary.dark,
                                                        opacity: 1,
                                                    },
                                                }}
                                            >
                                                {numberWithCommas(price)}
                                            </Typography >
                                        )}
                                    </Stack >

                                    <Typography
                                        noWrap
                                        fontSize = {16}
                                        sx = {{
                                            fontWeight: 500,
                                            fontSize: {md: price > 0 ? 20 : 16, xs: 16},
                                            justifyContent: "end",
                                            alignItems: "end",
                                            display: "flex",
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        <PriceRial price = {price} discount = {discount} />

                                    </Typography >
                                </Stack >
                            ) : (
                                user && (
                                    <Stack
                                        display = "flex"
                                        justifyContent = "end"
                                        flexDirection = "row"
                                        alignItems = "center"
                                        gap = {2}
                                        right = {140}
                                        bottom = {16}
                                        mb = {0.8}
                                        position = "absolute"
                                        divider = {<Divider orientation = "vertical" flexItem />}
                                    >
                                        <Typography
                                            fontSize = {14}
                                            fontWeight = {400}
                                            color = {theme.palette.primary.dark}
                                        >
                                            موجود نیست
                                        </Typography >

                                        <Typography
                                            fontSize = {14}
                                            fontWeight = {300}
                                            color = {theme.palette.primary.light}
                                        >
                                            ثبت درخواست پیش‌خرید
                                        </Typography >

                                    </Stack >
                                )
                            )}
                        </>
                    </Box >

                    <Stack
                        display = "flex"
                        alignSelf = "end"
                        height = "100%"
                        ml = {1}
                    >
                        <Box
                            display = "flex"
                            alignItems = "end"
                            gap = {2}
                            width = "100%"
                            height = "100%"
                            justifyContent = {user ? "end" : "space-between"}
                            mr = {user ? 0 : 2}
                        >
                            {user ? (
                                isExists ? (
                                    <PurchasingControl
                                        initialCount = {count}
                                        productName = {namePersian + " " + car}
                                        id = {id.toString()}
                                    />
                                ) : (
                                    <PurchasingControl
                                        productName = {namePersian + " " + car}
                                        id = {id.toString()}
                                        preOrder
                                        initialCount = {count}
                                    />
                                )
                            ) : (
                                <>
                                    <Typography fontSize = {14} fontWeight = {300} color = "primary" noWrap >
                                        برای مشاهده قیمت وارد شوید
                                    </Typography >
                                    <ProductLoginButton />
                                </>
                            )}
                        </Box >
                    </Stack >
                </Box >
            </Card >
        );
    };

    if (variant === "grid") {
        if (!slider && isMobile) {
            return renderListMobile();
        }

        return renderGrid();
    }


    return isMobile ? renderListMobile() : renderListDesktop();
}

export default memo(ProductCardUnified);
